// src/components/admin/performance/ReviewDetailsModal.js
import React from 'react';
import { X, Star, Calendar, User, FileText, MessageSquare } from 'lucide-react';

const ReviewDetailsModal = ({ review, onClose }) => {
    if (!review) return null;

    const skillRatings = [
        { key: 'technical_skills', label: 'Technical Skills' },
        { key: 'communication_skills', label: 'Communication Skills' },
        { key: 'teamwork', label: 'Teamwork & Collaboration' },
        { key: 'problem_solving', label: 'Problem Solving' },
        { key: 'leadership', label: 'Leadership' }
    ];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Performance Review Details</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {review.first_name} {review.last_name} • {review.employee_code}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Review Info */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-1">
                                <Calendar className="w-4 h-4 mr-2" />
                                Review Period
                            </div>
                            <div className="text-base font-medium text-gray-900 dark:text-white">
                                {new Date(review.review_period_start).toLocaleDateString()} - {new Date(review.review_period_end).toLocaleDateString()}
                            </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-1">
                                <User className="w-4 h-4 mr-2" />
                                Reviewer
                            </div>
                            <div className="text-base font-medium text-gray-900 dark:text-white">
                                {review.reviewer_first_name} {review.reviewer_last_name}
                            </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-1">
                                <FileText className="w-4 h-4 mr-2" />
                                Review Type
                            </div>
                            <div>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    review.review_type === 'self_review'
                                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                }`}>
                                    {review.review_type === 'self_review' ? 'Self Review' : 'Manager Review'}
                                </span>
                            </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-1">
                                <Star className="w-4 h-4 mr-2" />
                                Status
                            </div>
                            <div>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    review.status === 'completed'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                }`}>
                                    {review.status.charAt(0).toUpperCase() + review.status.slice(1)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Overall Rating */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
                        <div className="text-center">
                            <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Overall Rating</div>
                            <div className="flex items-center justify-center gap-2">
                                <Star className="w-8 h-8 text-yellow-400 fill-current" />
                                <span className="text-4xl font-bold text-gray-900 dark:text-white">
                                    {parseFloat(review.overall_rating).toFixed(1)}
                                </span>
                                <span className="text-2xl text-gray-500 dark:text-gray-400">/5.0</span>
                            </div>
                        </div>
                    </div>

                    {/* Skill Ratings */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Skill Ratings</h3>
                        <div className="space-y-4">
                            {skillRatings.map((skill) => (
                                <div key={skill.key}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            {skill.label}
                                        </span>
                                        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                            {parseFloat(review[skill.key] || 0).toFixed(1)}/5.0
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                        <div
                                            className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${(parseFloat(review[skill.key] || 0) / 5) * 100}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Feedback Sections */}
                    {review.strengths && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                                <MessageSquare className="w-5 h-5 mr-2 text-green-500" />
                                Key Strengths
                            </h3>
                            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                    {review.strengths}
                                </p>
                            </div>
                        </div>
                    )}

                    {review.areas_for_improvement && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                                <MessageSquare className="w-5 h-5 mr-2 text-orange-500" />
                                Areas for Improvement
                            </h3>
                            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                    {review.areas_for_improvement}
                                </p>
                            </div>
                        </div>
                    )}

                    {review.goals_for_next_period && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                                <MessageSquare className="w-5 h-5 mr-2 text-blue-500" />
                                Goals for Next Period
                            </h3>
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                    {review.goals_for_next_period}
                                </p>
                            </div>
                        </div>
                    )}

                    {review.manager_comments && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                                <MessageSquare className="w-5 h-5 mr-2 text-purple-500" />
                                Manager Comments
                            </h3>
                            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                    {review.manager_comments}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Template Info */}
                    {review.template_name && (
                        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                Template used: <span className="font-medium">{review.template_name}</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Created: {new Date(review.created_at).toLocaleString()}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReviewDetailsModal;
